/**
 * Stripe Payment Element の責務集約コンポーネント
 *
 * Stripe に関する知識（loadStripe / Elements / PaymentElement / options / Appearance）を
 * この1ファイルに閉じ込める。React Hook Form の Controller のように「Stripe の責務・知識を
 * 1箇所へ集約する」のが設計意図。
 *
 * presentations 層に Context Provider である Elements を置くのは、この集約のための意図的な例外。
 */
import { STRIPE_PUBLISHABLE_KEY } from '@fukuri-admins/constants/common';
import { Button, Stack } from '@fukuri-admins/ui/common';
import { Elements, PaymentElement } from '@stripe/react-stripe-js';
import type {
  Appearance,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import styles from './styles.module.scss';

// loadStripe はレンダーごとに呼ぶと iframe が再生成されるため、モジュールスコープで1回だけ呼ぶ
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Appearance（Figma のデザイントークンに寄せる）
const APPEARANCE: Appearance = {
  theme: 'stripe',
  variables: {
    fontFamily: "'Hiragino Sans', sans-serif",
    fontSizeBase: '14px',
    colorText: '#1d1d20',
    colorDanger: '#c70036',
    colorBackground: 'white',
    borderRadius: '8px',
    spacingUnit: '8px',
  },
  rules: {
    '.Label': {
      fontSize: '14px',
      color: '#1d1d20',
    },
    '.Input': {
      backgroundColor: 'white',
      border: '1px solid #d0d0d4',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#1d1d20',
      padding: '8px 12px',
      lineHeight: '24px', // 40px の高さを padding 8px 2つ + lineHeight 24px で実現
    },
    '.Input:focus': {
      borderColor: '#1d1d20',
    },
  },
};

// deferred モード: clientSecret なしで描画できる（SetupIntent は保存時に取得する。後続チケットのスコープ）
const ELEMENTS_OPTIONS: StripeElementsOptions = {
  mode: 'setup',
  currency: 'jpy',
  paymentMethodTypes: ['card'],
  locale: 'ja',
  appearance: APPEARANCE,
};

const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  // 国・郵便番号は収集しない（Figma に存在しないため）。
  // 'never' にしたフィールドは confirmSetup 時に billing_details として自前で渡す義務がある（後続チケットへの申し送り）
  fields: {
    billingDetails: {
      address: {
        country: 'never',
        postalCode: 'never',
      },
    },
  },
  // 規約文言は Figma にないため非表示
  terms: {
    card: 'never',
  },
  // Apple Pay / Google Pay / Link はデフォルト 'auto' で勝手に表示されるため明示的に無効化（Figma はプレーンなカードフォームのみ）
  wallets: {
    applePay: 'never',
    googlePay: 'never',
    link: 'never',
  },
};

export const StripePaymentField: React.FC = () => (
  <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
    <Stack direction='column' gap={24} className={styles.container}>
      <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
      {/* PaymentElement は送信ボタンを持たないため自前で配置する。押下時の配線（confirmSetup）は後続チケット */}
      <Button variant='primary' size='sm'>
        保存
      </Button>
    </Stack>
  </Elements>
);
